import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { servicesData } from '../../mocks/services';

const ServicesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-primary/5 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="font-serif text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Nos services professionnels
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Découvrez notre gamme complète de services de bien-être adaptés à vos besoins spécifiques
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesData.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/service/${service.slug}`}
                  className="group block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer h-full"
                >
                  <div className="relative h-72 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/20 to-transparent z-10"></div>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 w-14 h-14 flex items-center justify-center bg-white rounded-full shadow-lg z-20">
                      <i className={`${service.icon} text-3xl text-primary`}></i>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif font-semibold text-2xl text-gray-900 mb-3 group-hover:text-primary transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {service.shortDescription}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="space-y-1">
                        <div className="text-xs text-gray-500">Durée</div>
                        <div className="text-sm font-semibold text-primary">{service.duration}</div>
                      </div>
                      <div className="w-12 h-12 flex items-center justify-center bg-primary rounded-full group-hover:bg-accent transition-colors">
                        <i className="ri-arrow-right-line text-xl text-white"></i>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Pourquoi choisir HMC CLINIC ?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-user-star-line',
                title: 'Expertise professionnelle',
                description: 'Équipe de spécialistes certifiés et expérimentés'
              },
              {
                icon: 'ri-vr-line',
                title: 'Technologie innovante',
                description: 'VR Massage unique en RDC pour une expérience immersive'
              },
              {
                icon: 'ri-heart-line',
                title: 'Approche personnalisée',
                description: 'Plans de soins adaptés à vos besoins spécifiques'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Confidentialité garantie',
                description: 'Respect absolu de votre vie privée et de vos données'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-primary/10 rounded-full mx-auto mb-6">
                  <i className={`${feature.icon} text-3xl text-primary`}></i>
                </div>
                <h3 className="font-semibold text-xl text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900">
              Prêt à prendre soin de vous ?
            </h2>
            <p className="text-xl text-gray-600">
              Contactez-nous dès maintenant pour réserver votre première consultation
            </p>
            <a
              href="https://wa.me/243850465500"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-3 bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-semibold transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              <span>Prendre rendez-vous sur WhatsApp</span>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;